import React, {useState, useEffect, useMemo} from "react"
import {useCookies} from "react-cookie"
import {useTranslation} from "react-i18next"
import "../i18n"
import {useMediaQuery, Typography} from "@material-ui/core"
import {useOnUpdate, useTheme} from "../hooks"
import {makeStyles} from "@material-ui/core/styles"
import Pagination from "@material-ui/lab/Pagination"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4themesMaterial from "@amcharts/amcharts4/themes/material"
import am4themesDark from "@amcharts/amcharts4/themes/dark"
import am4themesAnimated from "@amcharts/amcharts4/themes/animated"
import {red, green, blue, orange, pink} from "@material-ui/core/colors"
import {useStore, useDispatch} from "../Store"
import {getTokenCookie, arrayInit1D, convertChartNorm, weekendJudge} from "../common"
import {getEquityPriceList, getFxPriceList, getComparisonSimilarityList} from "../api"

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottm: `16px`,
    fontSize: `16px`,
    color: orange.A400
  },
  pager: {
    "& > *": {
      margin: `0 auto`,
      textAlign: `center`,
      width: `100%`,
      marginTop: theme.spacing(2),
    },
  },
}))

/**
 * 標準化類似チャート表示クラス
 * @param {string} id - タグID
 */
export default function NormChart({id}) {
  const [cookies] = useCookies([])
  const {t} = useTranslation()
  const {
    store: {
      themeColor,
      mainList,
      perspective,
      subPerspective,
      locale,
      mainType,
      categoryList,
      currentTab,
      subTabs,
      forwardOption,
      categoryCodeList,
      inputCompareList,
      normPriceList,
      mainGroup,
      mainTarget,
      mainBaseDate,
      mainTerm,
      maDay,
      forecastDate,
      mainRiseFallDate,
      weightedAverage,
      similarExecFlag,
      chartUpdateFlag,
      getSimilar,
      page,
      loadingThreads,
      displayGraphs,
      normPage,
    },
  } = useStore()
  const {dispatch} = useDispatch()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const [chartData, setChartData] = useState([null, null, null])
  const [valueAxisData] = useState([null, null, null])
  const [currentTheme, setCurrentTheme] = useState(null) // 現在のテーマ(unuseTheme用)
  const isFx = mainType[perspective] === `FX` || mainType[perspective] === `FXSET`
  const classes = useStyles({
    isMobile,
  })

  /** 指定テーマカラーに対応するAm4テーマを返す */
  const getAm4Theme = (color) => {
    if (color === `dark`)
      return am4themesDark
    return am4themesMaterial
  }

  /** Am4全チャートを破棄 */
  const disposeChart = () => {
    am4core.disposeAllCharts()
  }

  /** 騰落率計算 */
  const riseFall = (priceList = null) => {
    if (!priceList) return
    if (priceList.forward.length === 0) return `0.00`
    if (mainRiseFallDate[perspective] === 0) return 0
    let baseDateValue = 1.0
    if (isFx) {
      const {middle = null, close = null} = (baseDateValue = priceList.backward.slice(-1)[0])
      if (middle) baseDateValue = middle
      if (close) baseDateValue = close
      if (!middle && !close) baseDateValue = 1.0
    } else {
      const {close = 1.0} = (baseDateValue = priceList.backward.slice(-1)[0])
      baseDateValue = close
    }
    const judge = mainRiseFallDate[perspective] <= priceList.forward.length ? true : false
    let riseFallDateValue = 1.0
    if (judge) {
      if (isFx) {
        const {middle = null, close = null} = priceList.forward[mainRiseFallDate[perspective] - 1]
        if (middle) riseFallDateValue = middle
        if (close) riseFallDateValue = close
        if (!middle && !close) riseFallDateValue = 1.0
      } else {
        const {close = 1.0} = priceList.forward[mainRiseFallDate[perspective] - 1]
        riseFallDateValue = close
      }
    } else {
      if (isFx) {
        const {middle = null, close = null} = priceList.forward.slice(-1)[0]
        if (middle) riseFallDateValue = middle
        if (close) riseFallDateValue = close
        if (!middle && !close) riseFallDateValue = 1.0
      } else {
        const {close = 1.0} = priceList.forward.slice(-1)[0]
        riseFallDateValue = close
      }
    }

    return 100 * (riseFallDateValue - baseDateValue) / baseDateValue
  }

  /** 騰落予測日 */
  const getForecastDate = (priceList) => {
    if (mainRiseFallDate[perspective] <= priceList.forwardNorm[subPerspective[perspective]].length) {
      forecastDate[perspective] = priceList.forwardNorm[subPerspective[perspective]][mainRiseFallDate[perspective] - 1].date
    } else {
      forecastDate[perspective] = priceList.forwardNorm[subPerspective[perspective]].slice(-1)[0].date
    }
    dispatch({
      type: `SET_FORECAST_DATE`,
      forecastDate,
    })
    return forecastDate
  }

  /** Am4XYグラフオブジェクトを生成し初期設定を行う */
  const createChart = () => {
    const chart = am4core.create(id, am4charts.XYChart)
    chart.showOnInit = false
    am4core.useTheme(getAm4Theme(themeColor))
    am4core.useTheme(am4themesAnimated)

    // const title = chart.titles.create()
    // title.text = t(`normalization`)
    // title.fontSize = 16
    // title.marginBottom = 16
    // title.fill = am4core.color(orange.A700)
    chart.cursor = new am4charts.XYCursor()
    chart.cursor.fill = am4core.color(theme.palette.primary.contrastText)
    chart.cursor.behavior = isMobile ? `none` : `zoomX`
    chart.leftAxesContainer.layout = `vertical`
    chart.svgContainer.htmlElement.style.height = isMobile ? `700px` : `650px`
    chart.svgContainer.htmlElement.style.width = `100%`
    chart.paddingLeft = isMobile ? 15 : 50
    chart.paddingRight = isMobile ? 15 : 50
    createLegends(chart)
    chart.exporting.menu = new am4core.ExportMenu()

     // bullet over より segment over を優先する
    chart.seriesContainer.zIndex = 10;

    // テーマカラー保存
    setCurrentTheme(themeColor)

    return chart
  }
  
  /** レジェンドを作成 */
  const createLegends = (chart) => {
    chart.legend = new am4charts.Legend()
    chart.legend.position = isMobile ? `bottom` : `right`
    chart.legend.scrollable = true
    chart.legend.itemContainers.template.events.on(`over`, (event) => {
      processOver(chart, event.target.dataItem.dataContext)
    })
    chart.legend.itemContainers.template.events.on(`out`, () => {
      processOut(chart)
    })
  }

  /** X軸(日付軸)を作成 */
  const createDateAxis = (chart) => {
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.groupData = !isFx
    dateAxis.minZoomCount = 5
    dateAxis.skipEmptyPeriods = weekendJudge(mainTerm[perspective].key)
    dateAxis.renderer.inside = false
    dateAxis.renderer.labels.template.fill = am4core.color(theme.palette.primary.contrastText)
    dateAxis.renderer.grid.template.strokeWidth = 1
    dateAxis.renderer.grid.template.strokeOpacity = 1
    dateAxis.dateFormats.setKey(`day`, `yyyy-MM-dd`)
    // dateAxis.dateFormats.setKey(`month`, `yyyy-MM`)
    dateAxis.periodChangeDateFormats.setKey(`month`, `[bold]yyyy`)
    dateAxis.title.fill = am4core.color(orange.A700)
    dateAxis.title.fontWeight = `bold`
    dateAxis.title.marginTop = 10
    return dateAxis
  }

  /** Y軸(標準化価格軸)を作成 */
  const createValueAxis = (chart, minValueAxis = 0, maxValueAxis = 1) => {
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    if (isMobile) valueAxis.renderer.labels.template.verticalCenter = `bottom`
    if (isMobile) valueAxis.renderer.labels.template.padding(2, 2, 2, 2)
    valueAxis.renderer.inside = false
    valueAxis.min = forwardOption[perspective] ? (minValueAxis < 0 ? minValueAxis : 0) : 0
    valueAxis.max = forwardOption[perspective] ? (maxValueAxis > 1 ? maxValueAxis : 1) : 1
    valueAxis.extraMin = 0
    valueAxis.extraMax = 0
    valueAxis.renderer.labels.template.dy = -0
    valueAxis.renderer.labels.template.fill = am4core.color(theme.palette.primary.contrastText)
    valueAxis.renderer.grid.template.strokeWidth = 1
    valueAxis.renderer.grid.template.strokeOpacity = 1
    valueAxis.height = am4core.percent(100)
    valueAxis.marginTop = 40
    return valueAxis
  }

  /** 価格折れ線を作成(ハイライト効果込み) */
  const createHighLightLineSeries = (chart, priceList, valueAxis, name, ranking) => {
    const lineSeries = chart.series.push(new am4charts.LineSeries())
    lineSeries.showOnInit = false;
    lineSeries.dataFields.dateX = `date`
    lineSeries.dataFields.valueY = subPerspective[perspective] + ranking
    lineSeries.name = name
    lineSeries.yAxis = valueAxis
    lineSeries.strokeWidth = 2
    lineSeries.stroke = ranking ? am4core.color(chart.colors.next()) : am4core.color(blue.A400)
    lineSeries.fill = ranking ? am4core.color(chart.colors.next()) : am4core.color(blue.A700)
    const data = forwardOption[perspective]
      ? priceList.backwardNorm[subPerspective[perspective]].concat(priceList.forwardNorm[subPerspective[perspective]])
      : priceList.backwardNorm[subPerspective[perspective]]
    lineSeries.data = data
    const bullet = lineSeries.bullets.push(new am4charts.CircleBullet())
    bullet.circle.radius = 3
    const segment = lineSeries.segments.template
    segment.interactionsEnabled = true
    const hoverState = segment.states.create(`hover`)
    hoverState.properties.strokeWidth = 6
    const dimmed = segment.states.create(`dimmed`)
    dimmed.properties.stroke = am4core.color(`#dadada`)
    dimmed.properties.strokeWidth = 1
    segment.events.on(`over`, (event) => {
      processOver(chart, event.target.dataItem.component)
    })
    segment.events.on(`out`, () => {
      processOut(chart)
    })
    return lineSeries
  }

  /* グラフホバー時の処理 */
  const processOver = (chart, hoveredSeries) => {
    hoveredSeries.toFront()
    hoveredSeries.segments.each(function (segment) {
      segment.setState(`hover`)
    })
    chart.series.each(function (series) {
      if (series.className !== `LineSeries`) return
      if (series !== hoveredSeries) {
        series.segments.each(function (segment) {
          segment.setState(`dimmed`)
        })
        series.bulletsContainer.setState(`dimmed`)
      }
    })
  }

  /* グラフホバーアウトの処理 */
  const processOut = (chart) => {
    chart.series.each(function (series) {
      if (series.className !== `LineSeries`) return
      series.segments.each(function (segment) {
        segment.setState(`default`)
      })
      series.bulletsContainer.setState(`default`)
    })
  }

  /** 縦軸ホバー時の処理(系列価格表示) */
  const createVerticalLineSeries = (chart, valueAxis, lineData, kind) => {
    const lineSeries = chart.series.push(new am4charts.LineSeries())
    lineSeries.showOnInit = false
    lineSeries.dataFields.dateX = `date`
    lineSeries.dataFields.valueY = subPerspective[perspective]
    lineSeries.yAxis = valueAxis
    lineSeries.strokeWidth = 10
    lineSeries.name = t(kind) /* i18next-extract-disable-line */
    lineSeries.tooltipText = `[bold font-size: 18px]${t(kind)}[/]` /* i18next-extract-disable-line */
    lineSeries.stroke = kind === `baseDate` ? am4core.color(orange.A400) : am4core.color(pink.A400)
    lineSeries.fill = kind === `baseDate` ? am4core.color(orange.A400) : am4core.color(pink.A400)
    lineSeries.data = lineData
  }

  /** 類似度データ取得 */
  const fetchSimilarData = async (inputData) => {
    let params = {}

    if (isFx) {
      params = {
        ...inputData,
        category: ``,
        code: inputData.code,
      } // category: `0000` or category: `` で自ペアとそれ以外
    } else {
      // 株式銘柄は、region、exchange、codeの組をパラメータとする。
      const record = mainList[perspective].find((data) => data.code === inputData.code)
      const {code = null, exchange = null, region = null} = record

      params = {
        ...inputData,
        code,
        exchange,
        region,
      }
    }

    // 取得limitを設定
    if (currentTab.comparisonLimit)
      params.comparisonLimit = currentTab.comparisonLimit

    // データを取得して返す
    return getComparisonSimilarityList(getTokenCookie(cookies), params)
  }

  /** FX時系列価格データ取得 */
  const fetchFxPriceList = async (inputCompareList) => {
    let normPriceSet = null

    await Promise.all(
      Object.entries(inputCompareList[perspective]).map(async ([index, inputCompare]) => {
        const ranking = index ? Number(index) : `#target`
        const {inputData = null} = inputCompare
        const data = await getFxPriceList(getTokenCookie(cookies), { ...inputData })
        const {backward, forward} = data[0]
        const {backwardNorm, forwardNorm} = convertChartNorm(data, ranking)
        const wardBuf = (ward) => ward.map(
          (data) => {
            const {movements, ...other} = data
            let maList = {}

            movements.forEach((movement, index) => (maList = {
                ...maList,
                [`movement${index}`]: Math.round(movement * 100) / 100,
              }),
            )

            return {
              ...maList,
              ...other,
            }
          }
        )
        const wardColored = (ward) => ward.map(
          (_, i) => {
            if (i > 0) {
              ward[i - 1].color = ward[i].middle > ward[i - 1].middle
                ? am4core.color(red[500]) : am4core.color(green.A700)
            }

            return ward[i]
          }
        )
        const backwardBuf = wardBuf(backward)
        const forwardBuf = wardBuf(forward)
        const backwardColored = wardColored(backwardBuf)
        const forwardColored = wardColored(forwardBuf)

        if (!ranking)
          normPriceSet = {
            ...data[0],
            backwardNorm,
            forwardNorm,
          }

        // ラインチャート：カラー設定：上昇：レッド、下落：グリーン

        inputCompareList[perspective][ranking] = {
          ranking,
          inputData: {
            ...inputData,
            compareStockNameJa: { },
            compareStockNameEn: { },
          },
          priceList: {
            code: data[0].code,
            baseDate: data[0].baseDate,
            backward: backwardColored,
            forward: forwardColored,
            backwardNorm,
            forwardNorm,
            ranking,
          },
        }
      }),
    )

    return normPriceSet
  }

  /** 時系列データAPIに渡すパラメータ組を返す */
  const getLoadingList = (inputCompareList) => {
    const l = inputCompareList[perspective]
    
    if (l.length <= loadingThreads)
      return l.map((v) => [ v ])
    else {
      const sliceList = (arr, size) => arr.flatMap((_, i, a) => i % size ? [] : [a.slice(i, i + size)])
      const count = Math.ceil(l.length / loadingThreads)

      return {
        count,
        list: sliceList(l, count)
      }
    }
  }

  /** 株式時系列データを取得する(10銘柄ずつまとめて取得する) */
  const fetchEquityPriceList = async (inputCompareList) => {
    const sections = getLoadingList(inputCompareList)
    let normPriceSet = null

    await Promise.all(
      Object.entries(sections.list).map(async ([secIndex, secList]) => {
        const inputDataList = secList.map((inputCompare) => inputCompare.inputData)
        const params = {
          type: mainType[perspective],
          term: mainTerm[perspective].key,
          movements: maDay[perspective],
          forward: true,
          parameters: inputDataList.map((inputData) => {
            return { 
              group: inputData.group,
              code: inputData.code,
              baseDate: inputData.baseDate
            }
          })
        }

        const rankList = await getEquityPriceList(getTokenCookie(cookies), params)

        Object.entries(rankList).map(([index, rankData]) => {
          const ranking = sections.count * Number(secIndex) + Number(index)
          const inputData = inputDataList.find((data) => data.code === rankData.code)
          const record = mainList[perspective].find((data) => data.code === inputData.code)
          const categoryRecord = record
            ? categoryList[perspective].find((category) => category.value === record.category)
            : `Undefined`
          const {backward, forward} = rankData
          const {backwardNorm, forwardNorm} = convertChartNorm([ rankData ], ranking)
          const wardBuf = (ward) => ward.map(
            (data) => {
              const {movements, ...other} = data
              let maList = {}
  
              movements.forEach((movement, index) => (maList = {
                  ...maList,
                  [`movement${index}`]: Math.round(movement * 100) / 100,
                }),
              )
  
              return {
                ...maList,
                ...other,
              }
            }
          )

          if (!ranking)
            normPriceSet = {
              ...rankData,
              backwardNorm,
              forwardNorm,
            }

          inputCompareList[perspective][ranking] = {
            ranking,
            inputData: {
              ...inputData,
              compareStockNameJa: record?.labelJa ?? ``,
              compareStockNameEn: record?.labelEn ?? ``,
              categoryNameJa: categoryRecord?.labelJa ?? ``,
              categoryNameEn: categoryRecord?.labelEn ?? ``,
            },
            priceList: {
              code: rankData.code,
              baseDate: rankData.baseDate,
              backward: wardBuf(backward),
              forward: wardBuf(forward),
              backwardNorm,
              forwardNorm,
              ranking,
            },
          }
        })
      }),
    )

    return normPriceSet
  }

  /** 時系列価格データ取得 */
  const fetchPriceList = async (inputCompareList) => {
    let normPriceSet = null

    if (isFx)
      normPriceSet = await fetchFxPriceList(inputCompareList)
    else
      normPriceSet = await fetchEquityPriceList(inputCompareList)

    let backwardMax = 0.0
    // eslint-disable-next-line no-unused-vars
    Object.entries(subTabs).forEach(([key, subTab]) => {
      normPriceSet.backwardNorm[subTab].forEach((data, idx1) => {
        inputCompareList[perspective].forEach((inputCompare) => {
          // From Ranging 1 to Ranking 10
          inputCompare.priceList.backwardNorm[subTab].forEach((item, idx2) => {
            if (idx1 !== idx2) return
            const kind = `${subTab}${item.ranking}`
            data[kind] = item[subTab]
            if (item[subTab] > backwardMax) backwardMax = item[subTab]
          })
        })
      })
      let forwardMax = 0.0
      normPriceSet.forwardNorm[subTab].forEach((data, idx1) => {
        inputCompareList[perspective].forEach((inputCompare) => {
          // From Ranging 1 to Ranking 10
          inputCompare.priceList.forwardNorm[subTab].forEach((item, idx2) => {
            if (idx1 !== idx2) return
            if (item[subTab] > forwardMax) forwardMax = item[subTab]
          })
        })
      })
      normPriceSet.forwardNorm[subTab].forEach((data, idx1) => {
        inputCompareList[perspective].forEach((inputCompare) => {
          // From Ranging 1 to Ranking 10
          inputCompare.priceList.forwardNorm[subTab].forEach((item, idx2) => {
            if (idx1 !== idx2) return
            const kind = `${subTab}${item.ranking}`
            data[kind] = item[subTab]
            if (!data[kind]) delete data[kind] // 0データの属性を削除
          })
          inputCompare.priceList.forward.forEach((item) => {
            if (!item[subTab]) delete item[subTab]
          })
        })
      })
    })
    inputCompareList[perspective].forEach((inputCompare, idx) => {
      // 0データの配列を削除
      inputCompareList[perspective][idx].priceList.forward = inputCompare.priceList.forward.filter(
        (data) => data.movement0 !== 0,
      )
    })
    normPriceList[perspective] = normPriceSet
    dispatch({
      type: `INPUT_COMPARE_LIST`,
      inputCompareList,
    })
    dispatch({
      type: `NORM_PRICE_LIST`,
      normPriceList,
    })

    return {
      inputCompareList,
    }
  }

  /** 基準日縦線生成 */
  const createBaseLine = (chartData, valueAxisData) => {
    if (!mainBaseDate[perspective].key) return
    const lineDataBaseDate = [
      {
        date: mainBaseDate[perspective].key,
        [subPerspective[perspective]]: 0.0,
      },
      {
        date: mainBaseDate[perspective].key,
        [subPerspective[perspective]]: 1.0,
      },
    ]
    if (mainBaseDate[perspective].key) {
      createVerticalLineSeries(chartData, valueAxisData, lineDataBaseDate, `baseDate`)
    }
  }

  /** 予測日縦線生成 */
  const createForecastLine = (forecastDate, chartData, valueAxisData) => {
    if (!forecastDate[perspective]) return
    const lineDataForecastDate = [
      {
        date: forecastDate[perspective],
        [subPerspective[perspective]]: 0.0,
      },
      {
        date: forecastDate[perspective],
        [subPerspective[perspective]]: 1.0,
      },
    ]
    if (forecastDate[perspective] && forwardOption[perspective]) {
      createVerticalLineSeries(chartData, valueAxisData, lineDataForecastDate, `forecastDate`)
    }
  }

  /** 類似度チャート取得メイン */
  const useMain = () => {
    const code = mainTarget[perspective].key
    const term = mainTerm[perspective].key
    const baseDate = mainBaseDate[perspective].key
    console.log(`loadingSimilarChart: `, code, term, baseDate)

    // コード、期間、基準日のどれかが指定なしの場合は処理しない
    if (!code || !term || !baseDate)
      return

    normPriceList[perspective] = null

    const inputData = {
      type: mainType[perspective],
      group: mainGroup[perspective],
      code: mainTarget[perspective].key,
      baseDate: mainBaseDate[perspective].key,
      term: mainTerm[perspective].key,
      riseFallDate: mainRiseFallDate[perspective],
      movements: maDay[perspective],
      category: categoryCodeList[perspective].value,
      // category: categoryCodeList[perspective].map((item) => item.value),
      forward: true,
    }

    // 類似度リスト読み出し
    fetchSimilarData(inputData).then((similarityList) => {
      console.log(similarityList.length, `of similarity list.`)
      
      if (similarityList.length === 0) {
        dispatch({
          type: `LOADING_MODAL`,
          loadingModal: {
            state: false,
          },
        })
        const confirmDialog = {
          message: t(`messageCompareEmpty`),
          state: true,
        }
        dispatch({
          type: `CONFIRM_DIALOG`,
          confirmDialog,
        })
        return
      }
      const loadingModal = {
        message: t(`messageCompareLoading`),
        state: true,
      }
      dispatch({
        type: `LOADING_MODAL`,
        loadingModal,
      })

      inputCompareList[perspective] = arrayInit1D(
        1 + (similarityList?.length ?? 0),
        { inputData, }
      )

      similarityList.forEach((similarity, ranking) => {
        const baseDate = similarity.comparisonBaseDate.split(` `)[0]
        const code = similarity.code
        const inputData = {
          ...inputCompareList[perspective][ranking + 1].inputData,
          baseDate,
          ...similarity,
          code,
        }
        inputCompareList[perspective][ranking + 1] = {
          inputData,
        }
      })

      let valueWeighted = 0.0
      let totalWeighted = 0.0

      // 価格データ読み出し
      fetchPriceList(inputCompareList)
        .then(({inputCompareList}) => {
          console.log(inputCompareList[perspective].length, `of price list.`)

          inputCompareList[perspective].forEach((inputCompare) => {
            const {ranking, inputData, priceList = null} = inputCompare
            const riseFallRate = riseFall(priceList)

            inputCompareList[perspective][ranking].priceList = {
              ...inputCompareList[perspective][ranking].priceList,
              riseFallRate,
            }

            const {similarity = 0.0} = inputData
            // const similar = Math.round(similarity * 10000) / 100
            const similar = similarity * 100
            valueWeighted += similar * riseFallRate
            totalWeighted += similar
          })

          const weightedValue = totalWeighted ? Math.round((valueWeighted / totalWeighted) * 100) / 100 : `----`

          weightedAverage[perspective] = weightedValue

          dispatch({
            type: `WEIGHTED_AVERAGE`,
            weightedAverage,
          })

          createNormChart(inputCompareList)
          similarExecFlag[perspective] = !similarExecFlag[perspective]

          dispatch({
            type: `SIMILAR_EXEC_FLAG`,
            similarExecFlag,
          })

          dispatch({
            type: `LOADING_MODAL`,
            loadingModal: {
              state: false,
            },
          })
        })
        .catch((error) => {
          dispatch({
            type: `LOADING_MODAL`,
            loadingModal: {
              state: false,
            },
          })

          const confirmDialog = {
            message: `${`error`}`,
            error,
            state: true,
          }

          dispatch({
            type: `CONFIRM_DIALOG`,
            confirmDialog,
          })
        })
    })
  }

  //useOnUpdate(useMain, [getSimilar])
  // 類似チャート取得イベント処理
  // getSimilartの変化をトリガーとする
  useEffect(() => {
    if (getSimilar) {
      useMain()
      dispatch({
        type: `GET_SIMILAR`,
        getSimilar: false,
      })
    }
  }, [getSimilar])

  /** 指定したページのコンペア銘柄データを抽出する */
  const getDisplayList = (dataList) => {
      const startIdx = 1 + displayGraphs * (normPage[perspective] - 1)

      return [ dataList[0] ].concat(dataList.slice(startIdx, startIdx + displayGraphs))
  }

  /** 標準化グラフ表示を作成する(本体) */
  const createNormChart = (inputCompareList) => {
    console.log(`Creating NormChart at`, normPage[perspective])

    // チャート領域削除
    disposeChart()
    dispatch({
      type: `CHART_UPDATE_FLAG`,
      chartUpdateFlag: !chartUpdateFlag,
    })
    
    // チャート領域作成
    chartData[perspective] = createChart()

    // グラフ軸作成
    const {minValueAxis, maxValueAxis} = getMinMaxValueAxis()
    createDateAxis(chartData[perspective])
    valueAxisData[perspective] = createValueAxis(chartData[perspective], minValueAxis, maxValueAxis)
    setChartData(chartData)

    const dataList = inputCompareList[perspective]

    if (!dataList || !normPriceList[perspective]) return
    if (!dataList[0]) return

    createBaseLine(chartData[perspective], valueAxisData[perspective])

    // 価格グラフ表示
    const selfPriceList = dataList[0]?.priceList ?? null

    if (selfPriceList?.backwardNorm && normPriceList[perspective]?.backwardNorm) {
      // メイン銘柄グラフ
      if (forwardOption[perspective]) {
        const forecastDate = getForecastDate(selfPriceList)

        createForecastLine(forecastDate, chartData[perspective], valueAxisData[perspective])
      }

      // コンペア銘柄グラフ(10ずつ)
      getDisplayList(dataList).forEach((inputCompare, idx) => {
        const seriesName = idx ? `Ranking ${inputCompare.ranking}` : `Target`

        createHighLightLineSeries(
          chartData[perspective],
          normPriceList[perspective],
          valueAxisData[perspective],
          seriesName,
          inputCompare.ranking,
        )
      })

      //setTimeout(() => { createLegends(chartData[perspective]) }, 0)
      setTimeout(() => { chartData[perspective].legend.reinit() }, 0)
    }

    setChartData(chartData)
  }

  /** 全標準化価格中の最大・最小値を取得する。
   * @description Y軸スケールの調整のため。
   */
  const getMinMaxValueAxis = () => {
    let minValueAxis = 0 //最小値デフォルト
    let maxValueAxis = 0 //最大値デフォルト

    // データ取得前
    if (!normPriceList[perspective])
      return { minValueAxis: -1.0, maxValueAxis: 1.0 }

    const secList = getDisplayList(inputCompareList[perspective])
    const sp = subPerspective[perspective] // 選択価格種別

    // 基準日以前
    secList.forEach((data) => {
      data.priceList.backwardNorm[sp].forEach((item) => {
        Object.entries(item).forEach((kv) => {
          if (kv[0] == sp) {
            if (kv[1] < minValueAxis) minValueAxis = kv[1]
            if (kv[1] > maxValueAxis) maxValueAxis = kv[1]
          }
        })
      })
    })

    //基準日以後
    if (forwardOption[perspective]) {
      secList.forEach((data) => {
        data.priceList.forwardNorm[sp].forEach((item) => {
          Object.entries(item).forEach((kv) => {
            if (kv[0] == sp) {
              if (kv[1] < minValueAxis) minValueAxis = kv[1]
              if (kv[1] > maxValueAxis) maxValueAxis = kv[1]
            }
          })
        })
      })
    }

    minValueAxis = Math.max(minValueAxis, -4.9) - 0.1
    maxValueAxis = Math.min(maxValueAxis, 4.9) + 0.1
    return { minValueAxis, maxValueAxis }
  }

  /** 標準化系列オブジェクト破棄 */
  // eslint-disable-next-line no-unused-vars
  const clearNormChart = () => {
    // eslint-disable-next-line no-unused-vars
    const _ = [...Array(3)].map((_, idx) => {
      const seriesLength = chartData[idx]?.series?.length ?? 0
      if (seriesLength) {
        for (let i = seriesLength - 1; i >= 0; i--) {
          chartData[idx].series.removeIndex(i).dispose()
        }
      }
    })
  }

  /** 縦線オブジェクト破棄 */
  // eslint-disable-next-line no-unused-vars
  const removeHighLightLine = (index) => {
    const seriesLength = chartData[perspective]?.series?.length ?? 0
    if (seriesLength) {
      for (let i = seriesLength - 1; i >= index; i--) {
        chartData[perspective].series.removeIndex(i).dispose()
      }
    }
  }

  /** 縦線オブジェクト更新 */
  // eslint-disable-next-line no-unused-vars
  const updateHighLightLine = () => {
    if (normPriceList[perspective]) {
      const dataList = inputCompareList[perspective]

      getDisplayList(dataList).forEach((inputCompare, idx) => {
        if (!inputCompare) return

        const seriesName = idx ? `Ranking ${inputCompare.ranking}` : `Target`

        createHighLightLineSeries(
          chartData[perspective],
          normPriceList[perspective],
          valueAxisData[perspective],
          seriesName,
          inputCompare.ranking,
        )
      })
    }

    const {minValueAxis, maxValueAxis} = getMinMaxValueAxis()

    valueAxisData[perspective].min = minValueAxis
    valueAxisData[perspective].max = maxValueAxis
    // setValueAxisData(valueAxisData)
  }

  /** ページ選択変更時の処理 */
  const handleChange = (event, value) => {
    normPage[perspective] = value
    clearNormChart()
    createNormChart(inputCompareList)

    dispatch({
      type: `NORM_PAGE`,
      value: normPage
    })
  }

  /** ページ選択ボタン */
  const paginate = useMemo(() => {
    const l = (inputCompareList[perspective]?.length ?? 1) - 1

    if (l > displayGraphs) {
      const count = Math.ceil(l  / displayGraphs)

      return <div className={classes.pager}>
        <Pagination
          count={count}
          variant="outlined"
          siblingCount={isMobile ? 0 : 5}
          boundaryCount={2}
          page={normPage[perspective]}
          onChange={handleChange}
        />
      </div>
    }
    else
      return <></>
  }, [ perspective, similarExecFlag[perspective], normPage[perspective], themeColor ])

  /** グラフ再表示 */
  useEffect(() => {
      if (page == `SIMILAR_CHART`) {
        if (currentTheme)
          am4core.unuseTheme(getAm4Theme(currentTheme))
  
        normPage[perspective] = 1
        dispatch({
          type: `NORM_PAGE`,
          value: normPage
        })

        clearNormChart()
        createNormChart(inputCompareList)
      }
    }, [
    perspective,
    subPerspective[perspective],
    forwardOption[perspective],
    page,
    locale,
    themeColor,
  ])

  return <>
    {<Typography className={classes.title}>{t(`normalization`)}</Typography>}
    { paginate }
    {<div id={id} />}
  </>
}
